import type { ReactNode } from 'react';
import type { VirtualAppointmentOverviewPatient } from '../../../services/virtualAppointmentsOverview';
import { useDmPatient } from '../../../hooks/useDmPatient';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { AppointmentDetailField } from '../appointments/AppointmentDetailField';
import { MISSING_VALUE } from './missingValue';
import { MONITORING_STATE_META, monitoringStateOf } from './monitoringState';

const WHOLE_DAYS = /^\d+$/;

function everyDays(days: number): string {
    return days === 1 ? 'Every day' : `Every ${days} days`;
}

// The SDI/RDI stores the planned cadence as a bare day count ("7"); anything else is shown as typed.
function formatPlannedFrequency(raw: string | null | undefined): string | null {
    const value = raw?.trim();
    if (!value) return null;
    return WHOLE_DAYS.test(value) ? everyDays(Number(value)) : value;
}

// Dental Monitoring's live interval wins; the SDI/RDI plan is the fallback, labelled as such.
function scanFrequencyText(
    live: number | null | undefined,
    planned: string | null | undefined
): string {
    if (live != null && live > 0) return everyDays(live);
    const plan = formatPlannedFrequency(planned);
    return plan ? `${plan} (planned)` : MISSING_VALUE;
}

// Aligner 0 is how fresh patients mirror, so it reads as unknown rather than "before aligner 1".
function formatAlignerProgress(current: number | null, total: number | null): string {
    const knownCurrent = current !== null && current > 0 ? String(current) : null;
    const knownTotal = total !== null && total > 0 ? String(total) : null;
    if (!knownCurrent && !knownTotal) return MISSING_VALUE;
    return knownTotal ? `${knownCurrent ?? '?'} / ${knownTotal}` : (knownCurrent ?? MISSING_VALUE);
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 px-3 py-2">
            <dt className="text-gray-500">{label}</dt>
            <dd className="min-w-0 truncate text-right text-gray-800">{children}</dd>
        </div>
    );
}

interface OverviewMonitoringSectionProps {
    patient: VirtualAppointmentOverviewPatient;
}

// Where the patient is in treatment, so an aligner trigger can be read against it.
export function OverviewMonitoringSection({ patient }: OverviewMonitoringSectionProps) {
    const { data: card, isLoading: cardLoading } = usePatientCard(patient.id);
    const dmLinked = card?.monitoring?.dmId != null;
    const { data: dmPatient, isLoading: dmLoading } = useDmPatient(
        dmLinked ? patient.id : undefined
    );
    const isLoading = cardLoading || (dmLinked && dmLoading);
    const meta = MONITORING_STATE_META[monitoringStateOf(patient.monitoringStatus)];
    return (
        <AppointmentDetailField label="Monitoring">
            <dl className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 text-sm">
                <Row label="Status">
                    <span className="inline-flex items-center gap-2">
                        <span
                            aria-hidden="true"
                            className={`h-2 w-2 rounded-full shrink-0 ${meta.dotClass}`}
                        />
                        <span className={meta.textClass}>{meta.label}</span>
                    </span>
                </Row>
                <Row label="Aligner">
                    {formatAlignerProgress(patient.currentAligner, patient.totalAligners)}
                </Row>
                <Row label="Scan frequency">
                    {isLoading ? (
                        <span
                            aria-label="Loading scan frequency"
                            className="inline-block h-3 w-20 bg-gray-200 rounded animate-pulse align-middle"
                        />
                    ) : (
                        scanFrequencyText(
                            dmPatient?.intervalDays,
                            card?.monitoring?.dmScanFrequency
                        )
                    )}
                </Row>
            </dl>
        </AppointmentDetailField>
    );
}
